import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Landmark, Wallet, Clock, CheckCircle2, AlertTriangle, RefreshCw, Loader2,
} from "lucide-react";

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function PayoutTimelinePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get("/stripe/connect/payouts/timeline");
      setData(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) setErr("connect_first");
      else setErr(detail || "Failed to load payout timeline");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp mb-6" data-testid="payout-timeline-loading">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2"/> Loading payout timeline…
    </div>
  );
  if (err === "connect_first") return null;    // Card only appears after onboarding.
  if (err) return (
    <div className="bg-white border border-rose-200 rounded-xl p-6 card-crisp mb-6" data-testid="payout-timeline-error">
      <div className="text-sm text-rose-700">{err}</div>
      <Button onClick={load} variant="outline" size="sm" className="mt-3" data-testid="payout-reload-btn">
        <RefreshCw className="w-4 h-4 mr-1"/> Retry
      </Button>
    </div>
  );
  if (!data) return null;

  const sched = data.schedule || {};
  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp mb-6 overflow-hidden" data-testid="payout-timeline-panel">
      <div className="p-4 sm:p-6 border-b border-zinc-100 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-emerald-700"/>
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold">Payouts</h2>
              <p className="text-sm text-zinc-500 mt-0.5">Available, pending, and upcoming payouts from your connected Stripe account.</p>
            </div>
          </div>
        </div>
        <Button onClick={load} variant="ghost" size="sm" data-testid="payout-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1"/> Refresh
        </Button>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Available" value={money(data.available)} icon={Wallet} tone="text-emerald-700" testid="payout-tile-available"/>
          <Tile label="Pending"   value={money(data.pending)}   icon={Clock}  tone="text-amber-700"   testid="payout-tile-pending"/>
          <Tile label="Est. next payout" value={money(data.estimated_next_amount)} icon={Landmark} tone="text-zinc-800" sub={data.next_payout ? fmtDate(data.next_payout.arrival_date) : "when balance clears"} testid="payout-tile-estimated"/>
          <Tile label="Last payout" value={money(data.last_payout?.amount || 0)} icon={CheckCircle2} tone="text-emerald-700" sub={data.last_payout ? fmtDate(data.last_payout.arrival_date) : "—"} testid="payout-tile-last"/>
        </div>

        {sched.interval && (
          <div className="text-[11px] text-zinc-500 uppercase tracking-widest font-mono-accent" data-testid="payout-schedule">
            Payout schedule: <span className="text-zinc-700">{sched.interval}{sched.weekly_anchor ? ` · ${sched.weekly_anchor}` : ""}{sched.delay_days ? ` · +${sched.delay_days}d` : ""}</span>
          </div>
        )}

        {data.failed_payouts?.length > 0 && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4" data-testid="payout-failed-alert">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-700"/>
              <div className="text-sm font-semibold text-rose-800">Failed payouts</div>
            </div>
            <ul className="space-y-1 text-xs text-rose-900">
              {data.failed_payouts.map((p) => (
                <li key={p.id} data-testid={`payout-failed-${p.id}`}>
                  {fmtDate(p.arrival_date)} · {money(p.amount)}
                  {p.failure_message ? <span className="italic"> — {p.failure_message}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, tone, sub, testid }) {
  return (
    <div className="rounded-lg border border-zinc-200 p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-zinc-500"/>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">{label}</div>
      </div>
      <div className={`text-lg font-bold ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}
